import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Mail, Loader2, Printer, FileSpreadsheet } from "lucide-react";
import { adminApi, mondayOf, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtMoney } from "@/components/status";
import StoreOrdersGrid from "./StoreOrdersGrid";

const DAYS = [["", "All days"], ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const q = (v) => (v == null ? "" : Number(v).toLocaleString("en-AU", { maximumFractionDigits: 2 }));

export default function StoreOrdersReport() {
  const [mode, setMode] = useState("date");
  const [isd, setIsd] = useState(() => new Date().toISOString().slice(0, 10));
  const [week, setWeek] = useState(mondayOf());
  const [day, setDay] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const reqRef = useRef(0);
  const args = mode === "date" ? [undefined, undefined, isd] : [week, day || undefined, undefined];

  const load = () => {
    const id = ++reqRef.current;
    adminApi.storeOrdersReport(...args).then((d) => { if (id === reqRef.current) setData(d); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    adminApi.storeOrdersLastEmailed(mode === "date" ? isd : "", mode === "week" ? week : "").then((d) => { if (id === reqRef.current) setLast(d); }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, isd, week, day]);

  const email = async () => {
    if (!window.confirm("Email this store orders report PDF to freshproduceNSW@metcash.com.au?")) return;
    setBusy(true);
    try { const r = await adminApi.emailStoreOrders(...args); toast.success(`Emailed to ${r.to.join(", ")} (CC ${r.cc.join(", ")})`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const hasData = !!data?.sections?.length;

  return (
    <div data-testid="admin-store-orders-report">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-[#28282E]">Store Orders Report</h1><p className="text-sm text-[#28282E]/70">Store × product matrix for Metcash distribution · editable · A3 landscape.</p></div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="store-orders-print-button" variant="outline" className="border-slate-200" onClick={() => window.open(adminApi.storeOrdersUrl(...args, "html"), "_blank")}><Printer className="mr-2 h-4 w-4" /> Print view</Button>
          <Button data-testid="store-orders-pdf-button" variant="outline" className="border-slate-200" onClick={() => window.open(adminApi.storeOrdersUrl(...args), "_blank")}><FileDown className="mr-2 h-4 w-4" /> Open PDF (A3)</Button>
          <Button data-testid="store-orders-xlsx-button" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => window.open(adminApi.storeOrdersUrl(...args, "xlsx"), "_blank")}><FileSpreadsheet className="mr-2 h-4 w-4" /> Download Excel</Button>
          <Button data-testid="store-orders-email-button" onClick={email} disabled={busy || !hasData} className="bg-[#EBBAD1] text-[#28282E] hover:bg-[#EBBAD1]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Email PDF to Metcash</Button>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-md border border-slate-200 p-0.5 text-sm">
            <button data-testid="store-orders-scope-date" onClick={() => setMode("date")} className={`rounded px-3 py-1.5 ${mode === "date" ? "bg-[#28282E] text-white" : "text-[#28282E]"}`}>Into-store date</button>
            <button data-testid="store-orders-scope-week" onClick={() => setMode("week")} className={`rounded px-3 py-1.5 ${mode === "week" ? "bg-[#28282E] text-white" : "text-[#28282E]"}`}>Week + delivery day</button>
          </div>
          {mode === "date" ? (
            <div><Label className="text-xs">Into store date</Label><Input data-testid="store-orders-date-input" type="date" value={isd} onChange={(e) => setIsd(e.target.value)} className="mt-1 w-44" /></div>
          ) : (<>
            <div><Label className="text-xs">Week commencing</Label><Input data-testid="store-orders-week-input" type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="mt-1 w-44" /></div>
            <div><Label className="text-xs">Delivery day</Label>
              <select data-testid="store-orders-day-select" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 block w-40 rounded-md border border-slate-200 px-2 py-2 text-sm">{DAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </>)}
          {data && (
            <div className="ml-auto flex gap-3">
              {[["Stores", data.store_count], ["Orders", data.order_count], ["Cartons", q(data.total_cartons)], ["PO total", fmtMoney(data.total_cost)]].map(([l, v]) => (
                <div key={l} data-testid={`store-orders-stat-${l.toLowerCase().replace(" ", "-")}`} className="rounded-xl bg-[#FBEEF4] px-4 py-2 text-center"><div className="text-xl font-bold text-[#28282E]">{v}</div><div className="text-[11px] text-[#28282E]/70">{l}</div></div>
              ))}
            </div>
          )}
        </div>
        {last?.sent_at && <p className="mt-3 text-xs text-[#28282E]/70" data-testid="store-orders-last-emailed">Last emailed to Metcash: {String(last.sent_at).slice(0, 16).replace("T", " ")} → {(last.to || []).join(", ")}</p>}
      </div>

      {!data ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#28282E]/50" /></div>
        : !hasData ? <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-[#28282E]/55">No orders in this scope.</div>
        : data.sections.map((sec) => <StoreOrdersGrid key={sec.into_store_date} section={sec} onChanged={load} />)}
    </div>
  );
}
